/**
 * Chat — TestOps Command's primary surface.
 *
 * Mounts @ytest/testops-command with TestOps-specific host bindings:
 *  - auth from this app's authStore
 *  - the package's own logo as the assistant avatar (no Athena branding)
 *  - portfolios still come from athena-api today (Knowledge Hub is athena-anchored)
 *  - execution-status lookups also go via athena-api
 */
import { useEffect } from 'react'
import { TestOpsCommand, TestOpsCommandLogo } from '@ytest/testops-command'
import { useAuthStore } from '../stores/authStore'
import { ScreenshotImage } from '../components/ScreenshotImage'
import { api } from '../services/api'
import { tokenStorage } from '@ytest/auth-client'

const ATHENA_API_URL = import.meta.env.VITE_ATHENA_API_URL || 'https://api.athena.ytest.ai'
const YTEST_URL = import.meta.env.VITE_YTEST_URL || 'https://ytest.ai'

interface PortfolioApiItem {
  id: string
  name: string
  description?: string | null
}

export function Chat() {
  const { user, tenantSlug, checkAuth } = useAuthStore()

  // If we got past ProtectedRoute but `user` is still null, the auth store
  // is in an inconsistent "token-says-yes-but-user-is-null" state. One
  // recovery attempt: re-run checkAuth. If that also leaves `user` null,
  // kick to login so the user actually sees a screen instead of a blank.
  useEffect(() => {
    if (user) return
    let cancelled = false
    void (async () => {
      try {
        await checkAuth()
      } finally {
        if (!cancelled && !useAuthStore.getState().user) {
          const returnUrl = encodeURIComponent(window.location.href)
          window.location.href = `${YTEST_URL}/login?return_to=${returnUrl}`
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user, checkAuth])

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Restoring your session…</p>
        </div>
      </div>
    )
  }

  return (
    <TestOpsCommand
      user={{
        id: user.id,
        full_name: user.full_name ?? null,
        email: user.email ?? null,
      }}
      tenant={{ id: user.tenant_id, name: tenantSlug ?? user.tenant_id }}
      AvatarComponent={TestOpsCommandLogo}
      ScreenshotComponent={ScreenshotImage}
      fetchPortfolios={async (opts) => {
        // Knowledge Hub portfolios live in athena-api today. Calling directly
        // (rather than through this app's `api` client) so that the package
        // sees the canonical shape without us re-exporting an athena DTO.
        const params = new URLSearchParams()
        if (opts?.limit) params.set('limit', String(opts.limit))
        const token = tokenStorage.getAccessToken()
        const res = await fetch(
          `${ATHENA_API_URL}/api/v1/portfolios${params.toString() ? `?${params}` : ''}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include',
          }
        )
        if (!res.ok) return []
        const data = (await res.json()) as { portfolios?: PortfolioApiItem[] }
        return (data.portfolios ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description ?? null,
        }))
      }}
      fetchExecutionStatus={async (id) => {
        return await api.get<{ status: string; duration_seconds?: number }>(
          `/api/v1/executions/${id}`
        )
      }}
    />
  )
}
